import { useAuth0 } from "@auth0/auth0-react";
import React, { useState, useContext, useEffect } from "react";
import { Message, useToaster, Toggle, Checkbox, CheckboxGroup } from "rsuite";
import styled from "styled-components";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import { SettingBody } from "../helpers/Admin_StyledComponents";
import { useParams } from "react-router-dom";
import { UserContext } from "../../_context/UserContext";
import { SeverityAlert } from "../../../../../appHelpers/appCommonSC";
import {
  Button,
  SectionRibbon,
  Picker,
  NoDataState,
  SpacedGlassCard,
  SectionTitle,
} from "../../../../ui";
import { colors } from "../../../../ui/theme/colors";
// Requiring the lodash library
const _ = require("lodash");

export const AlertSettingsFC = ({ user, baseSystems }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [userAlerts, setUserAlerts] = useState([]);
  const [displayUserAlerts, setDisplayUserAlerts] = useState([]);
  const [notifyEmail, setNotifyEmail] = useState(user.notify_email);
  const [notifySMS, setNotifySMS] = useState(user.notify_sms);
  const [loading, setLoading] = useState(false);
  const [resMsg, setResMsg] = useState("");
  const toaster = useToaster();
  const { userRoles } = useContext(UserContext);

  const [allAlertIDs, setAllAlertIDs] = useState([]);
  const [enabledAlerts, setEnabledAlerts] = useState([]);

  const handleCheckAll = (enabledAlerts, checked) =>
    setEnabledAlerts(checked ? allAlertIDs : []);

  const { id } = useParams();

  // Alert Filters
  const [fieldNameFilters, setFieldNameFilters] = useState([]);
  const [customerNameFilters, setCustomerNameFilters] = useState([]);
  const [severityFilters, setSeverityFilters] = useState([]);
  const [manufacturerFilters, setManufacturerFilters] = useState([]);
  const [modalityFilters, setModalityFilters] = useState([]);

  const [selectedFieldName, setSelectedFieldName] = useState([]);
  const [selectedCustomerName, setSelectedCustomerName] = useState([]);
  const [selectedSeverity, setSelectedSeverity] = useState([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState([]);
  const [selectedModality, setSelectedModality] = useState([]);

  const handleEmailToggle = async (e) => {
    setNotifyEmail(e);
    setLoading(true);

    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "POST",
        url: `/user/update/notify/email`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          notifyEmail: e,
          userEmail: user.email_address,
        }),
      });
      setResMsg(response.data);
      toaster.push(message, "topCenter");
    } catch (error) {
      console.error(error);
      toaster.push(errMessage, "topCenter");
    }
    setLoading(false);
  };

  const handleSMSToggle = async (e) => {
    setNotifySMS(e);

    setLoading(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "POST",
        url: `/user/update/notify/sms`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          notifysms: e,
          userEmail: user.email_address,
        }),
      });
      setResMsg(response.data);
      toaster.push(message, "topCenter");
    } catch (error) {
      console.error(error);
      toaster.push(errMessage, "topCenter");
    }
    setLoading(false);
  };

  const getUserAlertModels = async (e) => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "GET",
        url: `/alert/models/${id}`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      });

      setUserAlerts(response.data[0]);
      setDisplayUserAlerts(response.data[0]);
      setEnabledAlerts([
        ...new Set(
          response.data[0]
            ?.filter((alert) => alert.enabled)
            .map((model) => model.alert_id),
        ),
      ]);
      setAllAlertIDs([
        ...new Set(response.data[0]?.map((model) => model.alert_id)),
      ]);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const onSubmitGenerateUserAlerts = async (e) => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "POST",
        url: `/alert/generate/model`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          user_id: id,
        }),
      });
      setResMsg(response.data);
      toaster.push(message, "topCenter");
    } catch (error) {
      console.error(error);
      toaster.push(errMessage, "topCenter");
    }

    getUserAlertModels();
    setLoading(false);
  };

  const handleEnabled = async () => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "POST",
        url: `/alert/model/update/enabled`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          user_id: user.email_address,
          enabled_ids: enabledAlerts,
        }),
      });
      setResMsg(response.data);
      getUserAlertModels();
      toaster.push(message, "topCenter");
    } catch (error) {
      console.error(error);
      toaster.push(errMessage, "topCenter");
    }
  };

  const setFilters = () => {
    setFieldNameFilters(
      [...new Set(userAlerts.map((alert) => alert.field_name_alias))].map(
        (item) => ({ label: item, value: item }),
      ),
    );

    setCustomerNameFilters(
      [...new Set(userAlerts.map((alert) => alert.customer_name))].map(
        (item) => ({ label: item, value: item }),
      ),
    );

    setSeverityFilters(
      [...new Set(userAlerts.map((alert) => alert.severity))].map((item) => ({
        label: item,
        value: item,
      })),
    );

    setManufacturerFilters(
      [...new Set(userAlerts.map((alert) => alert.manufacturer))].map(
        (item) => ({ label: item, value: item }),
      ),
    );

    setModalityFilters(
      [...new Set(userAlerts.map((alert) => alert.modality))].map((item) => ({
        label: item,
        value: item,
      })),
    );
  };

  const handleUserFilters = () => {
    const tempMachines = userAlerts.filter(
      (machine) =>
        (selectedCustomerName.length === 0 ||
          selectedCustomerName.includes(machine.customer_name)) &&
        (selectedFieldName.length === 0 ||
          selectedFieldName.includes(machine.field_name_alias)) &&
        (selectedManufacturer.length === 0 ||
          selectedManufacturer.includes(machine.manufacturer)) &&
        (selectedSeverity.length === 0 ||
          selectedSeverity.includes(machine.severity)) &&
        (selectedModality.length === 0 ||
          selectedModality.includes(machine.modality)),
    );

    setAllAlertIDs([...new Set(tempMachines.map((model) => model.alert_id))]);

    setDisplayUserAlerts(tempMachines);
  };

  const handleReset = () => {
    setEnabledAlerts([
      ...new Set(
        userAlerts
          .filter((alert) => alert.enabled)
          .map((model) => model.alert_id),
      ),
    ]);
  };

  useEffect(() => {
    getUserAlertModels();
  }, []);

  useEffect(() => {
    setFilters();
    handleUserFilters();
  }, [userAlerts]);

  useEffect(() => {
    handleUserFilters();
  }, [
    selectedCustomerName,
    selectedFieldName,
    selectedManufacturer,
    selectedModality,
    selectedSeverity,
  ]);

  return (
    <SettingBody>
      <SectionRibbon title="Alert Settings" />

      <SpacedGlassCard>
        <SectionTitle>Notification Settings</SectionTitle>

        <NotificationGrid>
          <NotificationItem>
            <NotificationHeader>
              <NotificationLabel>Email Notifications</NotificationLabel>
              <Toggle
                checked={notifyEmail}
                onChange={handleEmailToggle}
                checkedChildren="Enabled"
                unCheckedChildren="Disabled"
              />
            </NotificationHeader>
            <NotificationDetail>
              Destination: {user.email_address}
            </NotificationDetail>
          </NotificationItem>

          <NotificationItem>
            <NotificationHeader>
              <NotificationLabel>SMS Notifications</NotificationLabel>
              <Toggle
                checked={notifySMS}
                onChange={handleSMSToggle}
                checkedChildren="Enabled"
                unCheckedChildren="Disabled"
              />
            </NotificationHeader>
            <NotificationDetail>
              Destination: {user.phone_number}
            </NotificationDetail>
          </NotificationItem>
        </NotificationGrid>
      </SpacedGlassCard>

      <SpacedGlassCard>
        <SectionTitle>Filter Alerts</SectionTitle>
        {customerNameFilters.length > 0 ||
        manufacturerFilters.length > 0 ||
        modalityFilters.length > 0 ||
        severityFilters.length > 0 ||
        fieldNameFilters.length > 0 ? (
          <FiltersGrid>
            {customerNameFilters.length > 0 && (
              <FilterItem>
                <Picker
                  data={customerNameFilters}
                  value={selectedCustomerName}
                  onChange={setSelectedCustomerName}
                  placeholder="Select customers..."
                  multiple={true}
                  searchable={false}
                  cleanable={true}
                />
              </FilterItem>
            )}

            {manufacturerFilters.length > 0 && (
              <FilterItem>
                <Picker
                  data={manufacturerFilters}
                  value={selectedManufacturer}
                  onChange={setSelectedManufacturer}
                  placeholder="Select manufacturers..."
                  multiple={true}
                  searchable={false}
                  cleanable={true}
                />
              </FilterItem>
            )}

            {modalityFilters.length > 0 && (
              <FilterItem>
                <Picker
                  data={modalityFilters}
                  value={selectedModality}
                  onChange={setSelectedModality}
                  placeholder="Select modalities..."
                  multiple={true}
                  searchable={false}
                  cleanable={true}
                />
              </FilterItem>
            )}

            {severityFilters.length > 0 && (
              <FilterItem>
                <Picker
                  data={severityFilters}
                  value={selectedSeverity}
                  onChange={setSelectedSeverity}
                  placeholder="Select severities..."
                  multiple={true}
                  searchable={false}
                  cleanable={true}
                />
              </FilterItem>
            )}

            {fieldNameFilters.length > 0 && (
              <FilterItem>
                <Picker
                  data={fieldNameFilters}
                  value={selectedFieldName}
                  onChange={setSelectedFieldName}
                  placeholder="Select fields..."
                  multiple={true}
                  searchable={false}
                  cleanable={true}
                />
              </FilterItem>
            )}
          </FiltersGrid>
        ) : (
          <NoDataState
            icon="🔍"
            title="No Filters Available Yet"
            message="Once you have alerts configured, you'll be able to filter them by customer, manufacturer, severity, and more. Generate some alerts first to unlock the full filtering experience!"
          />
        )}
      </SpacedGlassCard>

      <SpacedGlassCard>
        <SectionTitle>Alert Configuration</SectionTitle>

        <AlertControls>
          <ControlsLeft>
            <Checkbox
              indeterminate={
                enabledAlerts.length > 0 &&
                enabledAlerts.length < allAlertIDs.length
              }
              checked={enabledAlerts.length === allAlertIDs.length}
              onChange={handleCheckAll}
            >
              Enable All Alerts
            </Checkbox>
          </ControlsLeft>
          <ControlsRight>
            <Button variant="secondary" size="medium" onClick={handleReset}>
              Reset
            </Button>
            <Button variant="primary" size="medium" onClick={handleEnabled}>
              Save Changes
            </Button>
          </ControlsRight>
        </AlertControls>

        <AlertListHeader>
          <HeaderItem flex="3">System ID</HeaderItem>
          <HeaderItem flex="4">Manufacturer</HeaderItem>
          <HeaderItem flex="6">Customer</HeaderItem>
          <HeaderItem flex="3">Severity</HeaderItem>
          <HeaderItem flex="5">Message</HeaderItem>
          <HeaderItem flex="1">Enabled</HeaderItem>
        </AlertListHeader>

        <AlertList>
          {displayUserAlerts.length > 0 ? (
            <CheckboxGroup
              name="checkboxList"
              value={enabledAlerts}
              onChange={(value) => {
                console.log(value, "onChange");
                setEnabledAlerts(value);
              }}
            >
              {displayUserAlerts.map((alert, index) => (
                <AlertItem key={alert.alert_id || index}>
                  <AlertField flex="3">
                    <AlertPrimary>{alert.id}</AlertPrimary>
                    <AlertSecondary>{alert.cus_sys_id}</AlertSecondary>
                  </AlertField>

                  <AlertField flex="4">
                    <AlertPrimary>{alert.manufacturer}</AlertPrimary>
                    <AlertSecondary>{alert.model}</AlertSecondary>
                  </AlertField>

                  <AlertField flex="6">
                    <AlertPrimary>{alert.customer_name}</AlertPrimary>
                    <AlertSecondary>{alert.site_name}</AlertSecondary>
                  </AlertField>

                  <AlertField flex="3">
                    <SeverityAlert severity={alert.severity}>
                      {alert?.severity.toUpperCase()}
                    </SeverityAlert>
                  </AlertField>

                  <AlertField flex="5">
                    <AlertMessage>
                      {alert.field_name_alias} is{" "}
                      {alert.operator === "less_than"
                        ? "less than"
                        : alert.operator === "greater_than"
                          ? "greater than"
                          : alert.operator}{" "}
                      {alert.threshold} {alert.threshold_units}
                    </AlertMessage>
                  </AlertField>

                  <AlertField flex="1" center>
                    <Checkbox value={alert.alert_id} />
                  </AlertField>
                </AlertItem>
              ))}
            </CheckboxGroup>
          ) : (
            <NoDataState
              icon="🔔"
              title="No Alerts Configured"
              message={`Your alert dashboard is ready and waiting! Once you configure some alerts, you'll see them here where you can enable, disable, and manage them all in one place.${
                userRoles.includes("superuser")
                  ? " Use the 'Generate User Alerts' button below to get started!"
                  : ""
              }`}
            />
          )}
        </AlertList>
      </SpacedGlassCard>

      {userRoles.includes("superuser") && (
        <SpacedGlassCard>
          <SectionTitle>Developer Tools</SectionTitle>
          <Button
            variant="secondary"
            size="medium"
            onClick={onSubmitGenerateUserAlerts}
          >
            Generate User Alerts
          </Button>
        </SpacedGlassCard>
      )}
    </SettingBody>
  );
};

// Modern styled components for Alert Settings

const NotificationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 16px;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const NotificationItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const NotificationLabel = styled.span`
  font-weight: 500;
  font-size: 14px;
  color: ${colors.neutral[800]};
`;

const NotificationDetail = styled.span`
  font-size: 12px;
  color: ${colors.neutral[500]};
  font-style: italic;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 16px;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FilterItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const AlertControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px 0;
  border-bottom: 1px solid ${colors.neutral[200]};

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
`;

const ControlsLeft = styled.div`
  display: flex;
  align-items: center;
`;

const ControlsRight = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  @media screen and (max-width: 768px) {
    justify-content: center;
  }
`;

const AlertListHeader = styled.div`
  display: flex;
  padding: 12px 16px;
  background: ${colors.neutral[100]};
  border-radius: 8px;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${colors.neutral[600]};
`;

const HeaderItem = styled.div`
  flex: ${(props) => props.flex};
  display: flex;
  align-items: center;
`;

const AlertList = styled.div`
  border: 1px solid ${colors.neutral[200]};
  border-radius: 8px;
  overflow: hidden;
`;

const AlertItem = styled.div`
  display: flex;
  padding: 16px;
  border-bottom: 1px solid ${colors.neutral[200]};
  transition: background-color 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const AlertField = styled.div`
  flex: ${(props) => props.flex};
  display: flex;
  flex-direction: column;
  gap: 4px;
  ${(props) => props.center && "align-items: center; justify-content: center;"}
`;

const AlertPrimary = styled.span`
  font-weight: 500;
  font-size: 14px;
  color: ${colors.neutral[800]};
`;

const AlertSecondary = styled.span`
  font-size: 12px;
  color: ${colors.neutral[500]};
`;

const AlertMessage = styled.span`
  font-size: 13px;
  color: ${colors.neutral[700]};
  line-height: 1.4;
`;

const message = (
  <Message showIcon type="success" header="Success">
    You successfully updated profile.
  </Message>
);

const errMessage = (
  <Message showIcon type="error" header="Error">
    Something went wrong. Please try again.
  </Message>
);
